import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { cn } from '@/shared/lib/cn';
import {
  retornarShowroom,
  type InventarioRow,
  type ShowroomItem,
} from '../api';

const NO_PLACEMENT = '__none__';

// Toggle for the optional "Cerrar envío al Showroom" selector. Hidden
// for now — flip to true to re-enable when the placement audit trail
// is part of the workflow. Backend still creates the showroom rows on
// EnviarShowroom; nothing else needs to change to turn this back on.
const SHOW_PLACEMENT_SELECTOR = false;

/**
 * Move units from Prest/Show back to Disponible. Mirrors PowerApps's
 * PopUp_RetornarShowRoomArticulo but adds an optional placement
 * selector so admins can close out a specific `23.Showrooms` row when
 * they know which one is coming back.
 */
export function RetornarShowroomDialog({
  row,
  activePlacements,
  onClose,
}: {
  row: InventarioRow;
  activePlacements: ShowroomItem[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [cantidadRaw, setCantidadRaw] = useState('');
  const [showroomId, setShowroomId] = useState<string>(NO_PLACEMENT);

  const cantidad = parseCantidad(cantidadRaw);
  const prestShow = row.buckets.prestShow;
  const exceeds = cantidad !== null && cantidad > prestShow + 0.0001;
  const canSubmit =
    cantidad !== null && cantidad > 0 && !exceeds && prestShow > 0;

  const mutation = useMutation({
    mutationFn: () =>
      retornarShowroom(row.id, {
        cantidad: cantidad ?? 0,
        showroomId: showroomId === NO_PLACEMENT ? undefined : showroomId,
      }),
    onSuccess: (r) => {
      if (r.outcome === 'insufficient_prestshow' || r.outcome === 'showroom_not_found') {
        toast.error(r.message);
        return;
      }
      toast.success(`${r.cantidad} un. retornadas`);
      qc.invalidateQueries({ queryKey: ['inventario'] });
      onClose();
    },
    onError: () => toast.error('No se pudo retornar del showroom'),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent
        className="max-w-md"
        processing={mutation.isPending && 'Retornando del Showroom…'}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Undo2 className="h-4 w-4" aria-hidden />
            Retornar desde Showroom
          </DialogTitle>
          <DialogDescription>
            {row.fields.Articulo_I || 'artículo'} · Prest/Show actual:{' '}
            <strong>{prestShow}</strong> un.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 px-3 py-2">
            <Bucket label="Prest/Show" value={prestShow} tone="rose" />
            <ArrowRight className="h-5 w-5 text-muted-foreground" aria-hidden />
            <Bucket label="Disponible" value={row.buckets.disponible} tone="green" />
          </div>

          <div className="space-y-1">
            <Label htmlFor="ret-cantidad" className="text-sm">
              Cantidad a retornar <span className="text-destructive">*</span>
            </Label>
            <Input
              id="ret-cantidad"
              type="number"
              inputMode="decimal"
              min={0}
              max={prestShow}
              step="0.01"
              placeholder={`máx ${prestShow}`}
              value={cantidadRaw}
              onChange={(e) => setCantidadRaw(e.target.value)}
              className={cn('tabular-nums', exceeds && 'border-destructive')}
              autoFocus
            />
            {exceeds && (
              <p className="text-xs text-destructive">
                Excede Prest/Show ({prestShow}).
              </p>
            )}
          </div>

          {SHOW_PLACEMENT_SELECTOR && activePlacements.length > 0 && (
            <div className="space-y-1">
              <Label htmlFor="ret-placement" className="text-sm">
                Cerrar envío al Showroom{' '}
                <span className="text-xs font-normal text-muted-foreground">· opcional</span>
              </Label>
              <Select value={showroomId} onValueChange={setShowroomId}>
                <SelectTrigger id="ret-placement">
                  <SelectValue placeholder="Seleccionar envío..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_PLACEMENT}>
                    Sólo mover stock (no cerrar ningún envío)
                  </SelectItem>
                  {activePlacements.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      Envío #{p.id} · desde {p.fields.FechaShowRoom_SR || '—'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                Marca el envío seleccionado como finalizado al retornar el stock.
              </p>
            </div>
          )}

          {cantidad !== null && cantidad > 0 && !exceeds && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
              Después: Prest/Show {prestShow - cantidad} · Disponible{' '}
              {row.buckets.disponible + cantidad}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!canSubmit || mutation.isPending}
            className="gap-2"
          >
            <Undo2 className="h-4 w-4" aria-hidden />
            Confirmar retorno
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Bucket({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'rose' | 'green';
}) {
  const cls =
    tone === 'rose'
      ? 'bg-rose-100 text-rose-700'
      : 'bg-emerald-100 text-emerald-700';
  return (
    <div className="flex flex-1 flex-col items-center">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          'mt-1 inline-flex min-w-12 justify-center rounded-md px-2 py-0.5 text-sm font-semibold tabular-nums',
          cls,
        )}
      >
        {value}
      </span>
    </div>
  );
}

function parseCantidad(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : null;
}
